import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchTaxRates,
  createTaxRate,
  updateTaxRate,
  deleteTaxRate,
} from '../../api/taxRates';

export default function TaxRatesManager({ authConfig, onUnauthorized }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchTaxRates({}, authConfig);
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to load tax rates.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => {
    setEditingId(null);
    setName('');
    setRate('');
  };

  const startEdit = (item) => {
    setEditingId(item._id || item.id);
    setName(item.name || '');
    setRate(String(item.rate ?? item.percentage ?? ''));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const rateNum = parseFloat(rate);
    if (Number.isNaN(rateNum) || rateNum < 0) {
      setError('Enter a valid rate (e.g. 12).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { name: name.trim(), rate: rateNum };
      if (editingId) {
        await updateTaxRate(editingId, payload, authConfig);
      } else {
        await createTaxRate(payload, authConfig);
      }
      startAdd();
      await load();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tax rate?')) return;
    try {
      await deleteTaxRate(id, authConfig);
      await load();
      if (editingId === id) startAdd();
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      else setError(err?.response?.data?.message || 'Failed to delete.');
    }
  };

  if (loading) return <p className="text-gray-600">Loading tax rates...</p>;

  return (
    <div className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <form onSubmit={handleSave} className="flex flex-wrap items-end gap-2">
        <input
          type="text"
          placeholder="Name (e.g. VAT 12%)"
          className="border p-2 rounded text-sm flex-1 min-w-[120px]"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="Rate %"
          className="border p-2 rounded text-sm w-24"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
        />
        <button
          type="submit"
          disabled={saving}
          className="bg-indigo-600 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
        >
          {editingId ? 'Update' : 'Add'}
        </button>
        {editingId ? (
          <button type="button" onClick={startAdd} className="px-3 py-2 border rounded text-sm">
            Cancel
          </button>
        ) : null}
      </form>
      <ul className="border rounded divide-y">
        {list.length === 0 && <li className="p-2 text-sm text-gray-500">No tax rates yet.</li>}
        {list.map((item) => (
          <li key={item._id || item.id} className="p-2 flex items-center justify-between gap-2">
            <span className="text-sm">{item.name} – {Number(item.rate ?? item.percentage ?? 0)}%</span>
            <span className="flex gap-1">
              <button
                type="button"
                onClick={() => startEdit(item)}
                className="text-indigo-600 text-sm"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDelete(item._id || item.id)}
                className="text-red-600 text-sm"
              >
                Delete
              </button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
